import { create } from "zustand";
import { AlertStatusType, IAlertState } from "../interface/alert-state.interface";
import { initAlertState } from "../data/initialize/alert-states.data";
import { BootstrapColorType } from "../types/bootstrap.type";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import OwnershipCarType from "../types/ownershipCar.type";
import { initFileData } from "../data/initialize/file.data";



export type UpdateVehiclePermitFormValuesType = keyof IUpdateVehicleFormValues;
export type UpdateVehiclePermitFormErrorType = { [key in UpdateVehiclePermitFormValuesType]?: string };
export type UpdateVehiclePermitFormTouchedType = { [key in UpdateVehiclePermitFormValuesType]?: boolean };
export type UpdateVehiclePermitModalStepType = 'confirmation' | 'form';

export interface IUpdateVehiclePermitModalData {
    isModalOpen: boolean;
    modalStep: UpdateVehiclePermitModalStepType;
    alertState: IAlertState;
    requestId: number;
    personId: string;
    vehicleNum: string;
    otpCode: string;
    form: {
        values: IUpdateVehicleFormValues;
        errors: UpdateVehiclePermitFormErrorType;
        touched: UpdateVehiclePermitFormTouchedType;
    };
}

export interface IUpdateVehicleFormValues {
    newVehicleNum: string,
    newColorId: number,
    newTypeId: number,
    newVehicleGroupId: number,
    citizenComment: string,
    ownershipCar: number,
    scanCarId: IFileDataBase64,
    scanWorkStatment: IFileDataBase64
}

const initUpdateVehiclePermitModal: IUpdateVehiclePermitModalData = {
    isModalOpen: false,
    alertState: { ...initAlertState },
    modalStep: 'form',
    requestId: -1,
    personId: '',
    vehicleNum: '',
    otpCode: '',
    form: {
        values: {
            newVehicleNum: '',
            newColorId: -1,
            newTypeId: -1,
            newVehicleGroupId: -1,
            citizenComment: '',
            ownershipCar: OwnershipCarType.PRIVATE,
            scanCarId: initFileData,
            scanWorkStatment: initFileData
        },
        errors: {},
        touched: {},
    }
};

interface IUpdateVehiclePermitModalStore {
    updateVehiclePermitModalData: IUpdateVehiclePermitModalData;
    openVehiclePermitModal: (personId: string, vehicleNum: string, otpCode: string) => void;
    closeVehiclePermitModal: () => void;
    loadingUpdateVehiclePermitModal: () => void;
    setAlertState: (alertStatus: AlertStatusType, message?: string, alertColor?: BootstrapColorType | '') => void;
    setFieldBlurAndErrors: (field: UpdateVehiclePermitFormValuesType, errors: UpdateVehiclePermitFormErrorType) => void;
    setUpdateVehiclePermitValueForm: (field: UpdateVehiclePermitFormValuesType, value: any) => void;
    markAllUpdateVehiclePermitFormFieldsAsTouched: () => void;
    setModalUpdateVehiclePermitFormError: (errors: UpdateVehiclePermitFormErrorType) => void;
    resetModalStep: () => void;
    moveToConfirmationStep: (requestId: number) => void;
}

export const useUpdateVehiclePermitModalStore = create<IUpdateVehiclePermitModalStore>()((set) => ({
    updateVehiclePermitModalData: initUpdateVehiclePermitModal,
    openVehiclePermitModal: (personId: string, vehicleNum: string, otpCode: string) => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...initUpdateVehiclePermitModal,
            isModalOpen: true,
            personId: personId,
            vehicleNum: vehicleNum,
            otpCode: otpCode,
        },
    })),
    closeVehiclePermitModal: () => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            isModalOpen: false,
        },
    })),
    loadingUpdateVehiclePermitModal: () => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            alertState: {
                alertColor: '',
                message: '',
                alertStatus: 'loading',
            }
        },
    })),
    setAlertState: (
        alertStatus: AlertStatusType,
        message: string = '',
        alertColor: BootstrapColorType | '' = '') => set((state) => {

            if (alertStatus === 'loading' || alertStatus === 'noAlert') {
                message = '';
                alertColor = '';
            }

            if (alertStatus === 'hasAlert' && message === '') {
                alertStatus = 'noAlert';
            }

            if (alertStatus === 'hasAlert' && alertColor === '') {

                alertColor = 'danger';
            }

            return {
                ...state,
                updateVehiclePermitModalData: {
                    ...state.updateVehiclePermitModalData,
                    alertState: {
                        alertColor,
                        message,
                        alertStatus,
                    }
                },
            }
        }),
    setUpdateVehiclePermitValueForm: (
        field: UpdateVehiclePermitFormValuesType,
        value: any
    ) => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            form: {
                ...state.updateVehiclePermitModalData.form,
                values: {
                    ...state.updateVehiclePermitModalData.form['values'],
                    [field]: value,
                }
            }
        },
    })),
    setFieldBlurAndErrors: (
        field: UpdateVehiclePermitFormValuesType,
        errors: UpdateVehiclePermitFormErrorType
    ) => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            form: {
                ...state.updateVehiclePermitModalData.form,
                errors: { ...errors },
                touched: {
                    ...state.updateVehiclePermitModalData.form.touched,
                    [field]: true,
                }
            }
        },
    })),
    markAllUpdateVehiclePermitFormFieldsAsTouched: () => set((state) => {
        const allFieldsTouched: { [key in UpdateVehiclePermitFormValuesType]?: boolean } = {};
        (Object.keys(state.updateVehiclePermitModalData.form) as UpdateVehiclePermitFormValuesType[]).forEach((field) => {
            allFieldsTouched[field] = true;
        });
        return {
            ...state,
            updateVehiclePermitModalData: {
                ...state.updateVehiclePermitModalData,
                form: {
                    ...state.updateVehiclePermitModalData.form,
                    touched: allFieldsTouched,
                }
            },
        };
    }),
    setModalUpdateVehiclePermitFormError: (errors: UpdateVehiclePermitFormErrorType) => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            form: {
                ...state.updateVehiclePermitModalData.form,
                errors: errors,
            }
        },
    })),
    resetModalStep: () => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            modalStep: 'form',
        },
    })),
    moveToConfirmationStep: (requestId: number) => set((state) => ({
        ...state,
        updateVehiclePermitModalData: {
            ...state.updateVehiclePermitModalData,
            modalStep: 'confirmation',
            requestId: requestId,
            alertState: {
                alertStatus: 'noAlert',
                message: '',
                alertColor: '',
            }
        },
    })),
}));


export const useSetModalUpdateVehiclePermitFormBlurAndError = () => {
    return useUpdateVehiclePermitModalStore((state) => state.setFieldBlurAndErrors);
}

export const useSetUpdateVehiclePermitValueForm = () => {
    return useUpdateVehiclePermitModalStore((state) => state.setUpdateVehiclePermitValueForm);
}

export const useMarkAllUpdateVehiclePermitFormFieldsAsTouched = () => {
    return useUpdateVehiclePermitModalStore((state) => state.markAllUpdateVehiclePermitFormFieldsAsTouched);
}

export const useSetModalUpdateVehiclePermitFormError = () => {
    return useUpdateVehiclePermitModalStore((state) => state.setModalUpdateVehiclePermitFormError);
}

export const useLoadingUpdateVehiclePermitModal = () => {
    return useUpdateVehiclePermitModalStore((state) => state.loadingUpdateVehiclePermitModal);
}

export const useSetAlertStateUpdateVehiclePermitModal = () => {
    return useUpdateVehiclePermitModalStore((state) => state.setAlertState);
}

export const useOpenVehiclePermitModal = () => {
    return useUpdateVehiclePermitModalStore((state) => state.openVehiclePermitModal);
}

export const useCloseVehiclePermitModal = () => {
    return useUpdateVehiclePermitModalStore((state) => state.closeVehiclePermitModal);
}

export const useIsUpdateVehiclePermitModalOpen = () => {
    return useUpdateVehiclePermitModalStore((state) => state.updateVehiclePermitModalData.isModalOpen);
}

export const useGetUpdateVehiclePermitModalData = () => {
    return useUpdateVehiclePermitModalStore((state) => state.updateVehiclePermitModalData);
}

export const useGetAlertStateUpdateVehiclePermitModal = () => {
    return useUpdateVehiclePermitModalStore((state) => state.updateVehiclePermitModalData.alertState);
}

export const useGetUpdateVehiclePermitRequestId = () => {
    return useUpdateVehiclePermitModalStore((state) => state.updateVehiclePermitModalData.requestId);
}

export const useGetUpdateVehiclePermitModalStep = () => {
    return useUpdateVehiclePermitModalStore((state) => state.updateVehiclePermitModalData.modalStep);
}

export const useResetVehiclePermitModalStep = () => {
    return useUpdateVehiclePermitModalStore((state) => state.resetModalStep);
}

export const useVehiclePermitModalMoveToConfirmationStep = () => {
    return useUpdateVehiclePermitModalStore((state) => state.moveToConfirmationStep);
}
